import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";
import { Slider } from "./ui/slider";

export default function AudioPlayer() {
  const { currentTrack, isPlaying, progress, duration, volume, playTrack, seek, setVolumeLevel } =
    usePlayer();

  if (!currentTrack) return null;

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div
      data-testid="audio-player"
      className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up-player glass-surface border-t border-white/[0.06]"
    >
      {/* Gold progress line at top */}
      <div className="h-[2px] bg-[#1A1A1A] w-full relative">
        <div
          className="h-full bg-gold transition-[width] duration-100 ease-linear"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
        {/* Play/Pause */}
        <button
          data-testid="player-play-pause"
          onClick={() => playTrack(currentTrack)}
          className="metal-gold-surface flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-all hover:brightness-105"
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>

        {/* Track Info */}
        <div className="flex-shrink-0 min-w-0 w-40">
          <p className="text-sm text-[#EAEAEA] font-medium truncate">{currentTrack.title}</p>
          <p className="text-xs text-[#737373]">
            {currentTrack.bpm} BPM &middot; {currentTrack.key}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 flex items-center gap-3">
          <span className="text-xs text-[#737373] w-10 text-right">{formatTime(progress)}</span>
          <div
            data-testid="player-progress-bar"
            className="flex-1 h-6 flex items-center cursor-pointer group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = (e.clientX - rect.left) / rect.width;
              seek(x * duration);
            }}
          >
            <div className="w-full h-[3px] bg-[#1A1A1A] rounded-full relative">
              <div
                className="h-full bg-gold rounded-full transition-[width] duration-100 ease-linear"
                style={{ width: `${progressPercent}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progressPercent}% - 6px)` }}
              />
            </div>
          </div>
          <span className="text-xs text-[#737373] w-10">{formatTime(duration)}</span>
        </div>

        {/* Volume */}
        <div className="hidden md:flex items-center gap-2 flex-shrink-0 w-32">
          <button
            data-testid="player-mute-toggle"
            onClick={() => setVolumeLevel(volume > 0 ? 0 : 0.7)}
            className="text-[#737373] hover:text-[#EAEAEA] transition-colors"
          >
            {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <Slider
            data-testid="player-volume"
            value={[volume * 100]}
            max={100}
            step={1}
            onValueChange={([v]) => setVolumeLevel(v / 100)}
            className="w-20 [&_[role=slider]]:bg-gold [&_[role=slider]]:border-gold [&_.relative>div]:bg-gold"
          />
        </div>
      </div>
    </div>
  );
}
